import { z } from "zod";

import type { CreateClientPayload } from "@/services/clientService";
import type { CustomerDocumentType } from "@/services/clientService";
import { htmlVisibleLength } from "@/lib/html-text";
import {
  MAX_INVOICE_EMAIL_ADDRESSES,
  MAX_INVOICE_EMAIL_SERIALIZED_LENGTH,
  isValidInvoiceEmailSegment,
  serializeInvoiceEmailsForApi,
} from "@/utils/invoice-email-addresses";

export const clientFormSchema = z
  .object({
    legalName: z.string().min(1, "La razón social es obligatoria"),
    displayName: z.string().optional(),
    documentType: z.enum(["RUT", "CI", "EXT"]),
    rut: z.string(),
    countryCode: z.string().optional(),
    docRecepExt: z.string().max(20).optional(),
    address: z.string().optional(),
    ciudad: z.string().max(30).optional(),
    departamento: z.string().max(30).optional(),
    // Optional: a client invoiced on paper or in person has no address to
    // give. Still validated when something IS typed, so "optional" doesn't
    // quietly become "accepts anything".
    email: z
      .string()
      .optional()
      .refine(
        (v) =>
          v === undefined ||
          v === "" ||
          z.string().email().safeParse(v).success,
        "Formato de correo inválido",
      ),
    phone: z.string().optional(),
    defaultCurrency: z.enum(["USD", "UYU"]).optional(),
    sendInvoiceByEmail: z.boolean().optional(),
    autoSendInvoiceEmail: z.boolean().optional(),
    invoiceEmailChoice: z.enum(["contact", "different"]).optional(),
    invoiceEmail: z.array(z.string()).optional(),
    invoiceEmailCc: z.array(z.string()).optional(),
    invoiceEmailTemplate: z.enum(["default", "custom"]).optional(),
    invoiceEmailCustomBody: z.string().optional(),
    invoiceEmailCustomSubject: z
      .string()
      .max(255, "El asunto debe tener como máximo 255 caracteres")
      .optional(),
    invoiceEmailIncludeCompanyLogo: z.boolean().optional(),
    invoiceEmailIncludeInvoiceDetails: z.boolean().optional(),
    pdfFilenameTemplate: z.string().max(255).optional(),
    /** Marca this client's invoices default to; "" = none. */
    defaultBrandId: z.string().optional(),
    language: z.enum(["es", "en"]).optional(),
    paymentTermDays: z.union([
      z.literal(0),
      z.literal(30),
      z.literal(60),
      z.literal(90),
    ]).optional(),
    discountPercent: z
      .string()
      .optional()
      .transform((s) => {
        if (!s || s.trim() === "") return undefined;
        const n = Number(s);
        // null, not undefined: an unparseable value must stay distinguishable
        // from an absent one so the refine below can reject it.
        return Number.isNaN(n) ? null : n;
      })
      .refine(
        (v) => v === undefined || (v !== null && v >= 0 && v <= 100),
        "Debe estar entre 0 y 100",
      ),
  })
  .superRefine((data, ctx) => {
    const rutIsBlank = !data.rut?.trim();
    if (data.documentType === "RUT" || data.documentType === "CI") {
      if (rutIsBlank) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["rut"],
          message: "El número de documento es obligatorio",
        });
      }
    }
    if (data.documentType === "RUT" && !rutIsBlank) {
      const digits = data.rut.replace(/\D/g, "");
      if (digits.length !== 12) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["rut"],
          message: "El RUT debe tener 12 dígitos (solo números)",
        });
      }
    }
    if (data.documentType === "CI" && !rutIsBlank) {
      const digits = data.rut.replace(/\D/g, "");
      if (digits.length < 7 || digits.length > 8) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["rut"],
          message: "La CI debe tener 7 u 8 dígitos (solo números)",
        });
      }
    }
    if (data.documentType === "RUT") {
      if (!data.address?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["address"],
          message: "La dirección es obligatoria para clientes con RUT (empresas)",
        });
      }
    }
    if (data.documentType === "EXT") {
      if (!data.docRecepExt?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["docRecepExt"],
          message:
            "El documento del cliente extranjero es obligatorio (EIN, pasaporte, etc.)",
        });
      }
    }
    // Now that the contact email is optional, "send invoices to the contact
    // email" with no contact email is a silent no-op — the backend logs
    // "no recipient email; skip invoice" and nothing ever arrives. Catch it
    // here instead of letting the user discover it from a missing invoice.
    if (
      data.sendInvoiceByEmail === true &&
      data.invoiceEmailChoice !== "different" &&
      !data.email?.trim()
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["email"],
        message:
          "Agregá un correo de contacto o elegí un correo distinto para la factura",
      });
    }
    if (
      data.sendInvoiceByEmail === true &&
      data.invoiceEmailChoice === "different"
    ) {
      const emails = data.invoiceEmail ?? [];
      if (emails.length < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["invoiceEmail"],
          message:
            "Se requiere al menos un correo válido cuando se usa un correo distinto para la factura",
        });
      }
      if (emails.length > MAX_INVOICE_EMAIL_ADDRESSES) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["invoiceEmail"],
          message: `Se permiten como máximo ${MAX_INVOICE_EMAIL_ADDRESSES} direcciones de correo`,
        });
      }
      const invalid = emails.find((e) => !isValidInvoiceEmailSegment(e));
      if (invalid !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["invoiceEmail"],
          message: "Cada dirección debe ser un correo válido",
        });
      }
      const serialized = serializeInvoiceEmailsForApi(emails);
      if (serialized.length > MAX_INVOICE_EMAIL_SERIALIZED_LENGTH) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["invoiceEmail"],
          message: "La lista de correos para la factura es demasiado larga",
        });
      }
    }
    if (
      data.sendInvoiceByEmail === true &&
      data.invoiceEmailChoice === "different" &&
      (data.invoiceEmailCc?.length ?? 0) > 0
    ) {
      const ccEmails = data.invoiceEmailCc ?? [];
      if (ccEmails.length > MAX_INVOICE_EMAIL_ADDRESSES) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["invoiceEmailCc"],
          message: `Se permiten como máximo ${MAX_INVOICE_EMAIL_ADDRESSES} direcciones en CC`,
        });
      }
      const invalidCc = ccEmails.find((e) => !isValidInvoiceEmailSegment(e));
      if (invalidCc !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["invoiceEmailCc"],
          message: "Cada dirección en CC debe ser un correo válido",
        });
      }
      const serializedCc = serializeInvoiceEmailsForApi(ccEmails);
      if (serializedCc.length > MAX_INVOICE_EMAIL_SERIALIZED_LENGTH) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["invoiceEmailCc"],
          message: "La lista de correos en CC es demasiado larga",
        });
      }
    }
    if (
      data.sendInvoiceByEmail === true &&
      data.invoiceEmailTemplate === "custom"
    ) {
      const bodyLength = htmlVisibleLength(data.invoiceEmailCustomBody);
      if (bodyLength < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["invoiceEmailCustomBody"],
          message: "Agregá un mensaje para la plantilla de correo personalizada",
        });
      }
      if (bodyLength > 2000) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["invoiceEmailCustomBody"],
          message: "El mensaje debe tener como máximo 2000 caracteres",
        });
      }
    }
  });

export type ClientFormValues = z.input<typeof clientFormSchema>;

export const defaultClientFormValues: ClientFormValues = {
  legalName: "",
  displayName: "",
  documentType: "RUT",
  rut: "",
  countryCode: "",
  docRecepExt: "",
  address: "",
  ciudad: "",
  departamento: "",
  email: "",
  phone: "",
  defaultCurrency: "UYU",
  paymentTermDays: 0,
  discountPercent: "",
  sendInvoiceByEmail: false,
  autoSendInvoiceEmail: true,
  invoiceEmailChoice: "contact",
  invoiceEmail: [],
  invoiceEmailCc: [],
  invoiceEmailTemplate: "default",
  invoiceEmailCustomBody: "",
  invoiceEmailCustomSubject: "",
  invoiceEmailIncludeCompanyLogo: true,
  invoiceEmailIncludeInvoiceDetails: true,
  pdfFilenameTemplate: "",
  language: "es",
  defaultBrandId: "",
};

export const CLIENT_DOCUMENT_TYPES: {
  value: CustomerDocumentType;
  labelKey: string;
}[] = [
  { value: "RUT", labelKey: "clients.form.documentTypeRut" },
  { value: "CI", labelKey: "clients.form.documentTypeCi" },
  { value: "EXT", labelKey: "clients.form.documentTypeExt" },
];

export const CLIENT_PAYMENT_TERM_OPTIONS = [
  { value: 0 as const, labelKey: "clients.form.paymentTerm0" },
  { value: 30 as const, labelKey: "clients.form.paymentTerm30" },
  { value: 60 as const, labelKey: "clients.form.paymentTerm60" },
  { value: 90 as const, labelKey: "clients.form.paymentTerm90" },
];

export const CLIENT_CURRENCY_OPTIONS = [
  { value: "USD" as const, labelKey: "clients.form.defaultCurrencyUsd" },
  { value: "UYU" as const, labelKey: "clients.form.defaultCurrencyUyu" },
];

export const CLIENT_COUNTRY_OPTIONS: { value: string; label: string }[] = [
  { value: "UY", label: "Uruguay" },
  { value: "AR", label: "Argentina" },
  { value: "BR", label: "Brasil" },
  { value: "US", label: "Estados Unidos" },
  { value: "CL", label: "Chile" },
  { value: "PY", label: "Paraguay" },
  { value: "ES", label: "España" },
  { value: "OTHER", label: "Otro" },
];

export function normalizeClientRut(value: string): string {
  return value.replace(/\D/g, "").padStart(12, "0").slice(-12);
}

export function normalizeClientCi(value: string): string {
  return value.replace(/\D/g, "").slice(0, 8);
}

export function buildCreateClientPayload(
  values: ClientFormValues,
  options: { canUseInvoiceEmailTemplates: boolean }
): CreateClientPayload {
  const { canUseInvoiceEmailTemplates } = options;
  const payload: CreateClientPayload = {
    legalName: values.legalName.trim().toUpperCase(),
    displayName: values.displayName?.trim() || undefined,
    documentType: values.documentType,
    address: values.address?.trim() || undefined,
    ciudad: values.ciudad?.trim() || undefined,
    departamento: values.departamento?.trim() || undefined,
    email: values.email?.trim() || undefined,
    phone: values.phone?.trim() || undefined,
    // null, not undefined, when cleared: the API treats undefined as "leave
    // alone" and null as "clear it".
    defaultBrandId: values.defaultBrandId?.trim() || null,
  };
  if (values.documentType === "EXT") {
    payload.countryCode = values.countryCode?.trim() || undefined;
    payload.docRecepExt = values.docRecepExt?.trim()?.slice(0, 20) || undefined;
  } else {
    payload.rut =
      values.documentType === "RUT"
        ? normalizeClientRut(values.rut)
        : normalizeClientCi(values.rut);
  }
  if (values.defaultCurrency !== undefined)
    payload.defaultCurrency = values.defaultCurrency;
  if (values.paymentTermDays !== undefined)
    payload.paymentTermDays = values.paymentTermDays;
  if (values.discountPercent !== undefined && String(values.discountPercent).trim() !== "") {
    const n = Number(values.discountPercent);
    // Omit the key entirely rather than sending an explicit undefined: an
    // unparseable discount is rejected by the schema before it reaches here.
    if (!Number.isNaN(n)) payload.discountPercent = n;
  }
  if (values.sendInvoiceByEmail !== undefined)
    payload.sendInvoiceByEmail = values.sendInvoiceByEmail;
  if (values.autoSendInvoiceEmail !== undefined)
    payload.autoSendInvoiceEmail = values.autoSendInvoiceEmail;
  if (
    values.sendInvoiceByEmail &&
    values.invoiceEmailChoice === "different" &&
    values.invoiceEmail &&
    values.invoiceEmail.length > 0
  ) {
    payload.invoiceEmail = serializeInvoiceEmailsForApi(values.invoiceEmail);
  } else {
    payload.invoiceEmail = null;
  }
  if (
    values.sendInvoiceByEmail &&
    values.invoiceEmailChoice === "different"
  ) {
    const ccList = values.invoiceEmailCc?.filter((e) => e.trim()) ?? [];
    payload.invoiceEmailCc =
      ccList.length > 0 ? serializeInvoiceEmailsForApi(ccList) : null;
  } else {
    payload.invoiceEmailCc = null;
  }
  if (values.sendInvoiceByEmail === true && canUseInvoiceEmailTemplates) {
    payload.invoiceEmailTemplate = values.invoiceEmailTemplate ?? "default";
    if (values.invoiceEmailTemplate === "custom") {
      payload.invoiceEmailCustomBody = values.invoiceEmailCustomBody?.trim() ?? "";
      payload.invoiceEmailCustomSubject =
        values.invoiceEmailCustomSubject?.trim() || undefined;
      payload.invoiceEmailIncludeCompanyLogo =
        values.invoiceEmailIncludeCompanyLogo ?? true;
      payload.invoiceEmailIncludeInvoiceDetails =
        values.invoiceEmailIncludeInvoiceDetails ?? true;
    }
  }
  if (values.pdfFilenameTemplate?.trim())
    payload.pdfFilenameTemplate = values.pdfFilenameTemplate.trim();
  if (values.language) payload.language = values.language;
  return payload;
}
